// BM-H3669 - question primitives for the LPA questionnaire.
//
// Every question the client sees renders through LpaQuestion (one label, one
// tooltip) or LpaOption (one radio or checkbox choice, one tooltip), and every
// section opens with LpaIntro. Each carries the question id in data-qid (or the
// section id in data-intro), so the parity test can prove that nothing the
// client can answer is missing its explanation, and that no explanation is
// registered for a question that is never shown.
//
// This is deliberately the same shape as the Wills QuestionRow
// (components/client-forms/wills/fields.jsx), reusing the shared FieldTooltip,
// so the two questionnaires behave alike. No data is read or written here.

import FieldTooltip from "../FieldTooltip";
import { getIntro, getTooltip, hasIntro, hasTooltip } from "./lpaTooltipRegistry";

const IS_PRODUCTION = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production";

// A missing explanation must be absent and loud in the logs, never present and
// empty. In dev and test an unregistered id throws. The parity test makes that
// unreachable in a shipped build.
export function lpaTooltipFor(qid) {
  if (!hasTooltip(qid)) {
    if (IS_PRODUCTION) {
      console.error(`lpaTooltipRegistry: no tooltip registered for question ${qid}`);
      return null;
    }
    return getTooltip(qid);
  }
  return getTooltip(qid);
}

export function lpaIntroFor(id) {
  if (!hasIntro(id)) {
    if (IS_PRODUCTION) {
      console.error(`lpaTooltipRegistry: no section intro registered for ${id}`);
      return null;
    }
    return getIntro(id);
  }
  return getIntro(id);
}

// The tooltip trigger on its own, for places that lay out their own label.
export function LpaTip({ qid, label }) {
  const text = lpaTooltipFor(qid);
  if (!text) return null;
  return (
    <FieldTooltip
      text={text}
      testid={`tooltip-${qid}`}
      ariaLabel={label ? `More information: ${label}` : "More information"}
    />
  );
}

const labelBase = { fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 };

// A question: a label with its tooltip, and the controls (or nested questions)
// that answer it.
export function LpaQuestion({ qid, label, children, style, labelStyle, testid }) {
  return (
    <div data-qid={qid} data-testid={testid} style={style}>
      <label style={{ ...labelBase, ...labelStyle }}>
        {label}
        <LpaTip qid={qid} label={typeof label === "string" ? label : undefined} />
      </label>
      {children}
    </div>
  );
}

// One choice inside a group: the radio or checkbox and its words are the
// children of the label, and the tooltip sits beside the label so that
// clicking the tooltip never toggles the choice. `below` renders under the row
// and is still inside this choice's wrapper (for example the phone number box
// that belongs to the "By phone" choice).
export function LpaOption({ qid, label, children, style, labelStyle, labelTestId, below }) {
  return (
    <div data-qid={qid} style={style}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <label data-testid={labelTestId} style={{ fontSize: 13, ...labelStyle }}>
          {children}
        </label>
        <LpaTip qid={qid} label={label} />
      </div>
      {below}
    </div>
  );
}

const introStyle = { fontSize: 13, color: "#334155", margin: "0 0 12px" };

// The short introduction at the top of a section.
export function LpaIntro({ id }) {
  const text = lpaIntroFor(id);
  if (!text) return null;
  return (
    <div data-intro={id} data-testid={`lpa-intro-${id}`}>
      <p style={introStyle}>{text}</p>
    </div>
  );
}
